import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminCourses, getAdminEditions } from '../../api/courses';
import { adminManualEnroll } from '../../api/enrollments';
import type { RegistrationField } from '../../types';

export default function AdminManualEnrollPage() {
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [email, setEmail] = useState('');
  const [azienda, setAzienda] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [extraData, setExtraData] = useState<Record<string, string | boolean | number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successEnrollments, setSuccessEnrollments] = useState<{ enrollmentId: string }[] | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: courses = [], isLoading: loadingCourses } = useQuery({
    queryKey: ['admin-courses'],
    queryFn: getAdminCourses,
  });

  const { data: editions = [], isLoading: loadingEditions } = useQuery({
    queryKey: ['admin-editions'],
    queryFn: getAdminEditions,
  });

  const isLoading = loadingCourses || loadingEditions;

  // Group editions by courseId
  const editionsByCourse = useMemo(() => {
    const map = new Map<string, typeof editions>();
    for (const e of editions) {
      if (!map.has(e.courseId)) map.set(e.courseId, []);
      map.get(e.courseId)!.push(e);
    }
    return map;
  }, [editions]);

  // Collect unique registration fields from all courses
  const customFields = useMemo<RegistrationField[]>(() => {
    const seen = new Set<string>();
    const result: RegistrationField[] = [];
    for (const course of courses) {
      for (const f of (course.registrationFields ?? [])) {
        if (!seen.has(f.key)) { seen.add(f.key); result.push(f); }
      }
    }
    return result;
  }, [courses]);

  const visibleCustomFields = useMemo<RegistrationField[]>(() => {
    return customFields.filter(f => {
      if (f.key.startsWith('giornata_')) return false;
      if (['privacy', 'autorizzazione_foto'].includes(f.key)) return false;
      if (!f.showIf) return true;
      return String(extraData[f.showIf.key] ?? '') === f.showIf.value;
    });
  }, [customFields, extraData]);

  const toggleEdition = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setErrors(e => ({ ...e, editions: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!nome.trim()) errs.nome = 'Il nome è obbligatorio';
    if (!cognome.trim()) errs.cognome = 'Il cognome è obbligatorio';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = 'Inserisci un indirizzo email valido';
    if (selectedIds.size === 0)
      errs.editions = 'Seleziona almeno un\'edizione';
    for (const f of visibleCustomFields) {
      if (f.required && !extraData[f.key])
        errs[`extra_${f.key}`] = `${f.label} è obbligatorio`;
    }
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const mergedExtra: Record<string, string | boolean | number> = {};
      for (const [k, v] of Object.entries(extraData)) {
        if (k.endsWith('__altro')) continue;
        if (v === `__altro__${extraData[`${k}__altro`] ?? ''}`) {
          mergedExtra[k] = String(extraData[`${k}__altro`] ?? '');
        } else {
          mergedExtra[k] = v;
        }
      }
      const result = await adminManualEnroll({
        nome: nome.trim(),
        cognome: cognome.trim(),
        email: email.trim().toLowerCase(),
        azienda: azienda.trim() || undefined,
        edition_ids: Array.from(selectedIds),
        extra_data: Object.keys(mergedExtra).length > 0 ? mergedExtra : undefined,
      });
      setSuccessEnrollments(result.enrollments);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSubmitError(msg ?? 'Si è verificato un errore. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setNome(''); setCognome(''); setEmail(''); setAzienda('');
    setSelectedIds(new Set()); setExtraData({}); setErrors({});
    setSuccessEnrollments(null); setSubmitError(null);
  };

  if (successEnrollments) {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">Iscrizione manuale</h1>
        </div>
        <div className="card" style={{ maxWidth: 560 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: '1.0625rem' }}>
              Iscrizione creata con successo per {successEnrollments.length} {successEnrollments.length === 1 ? 'edizione' : 'edizioni'}.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {successEnrollments.map(en => (
                <Link key={en.enrollmentId} to={`/admin/enrollments/${en.enrollmentId}`} className="btn btn-ghost btn-sm" style={{ justifyContent: 'flex-start' }}>
                  Vai all'iscrizione →
                </Link>
              ))}
            </div>
            <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={handleReset}>
              Nuova iscrizione
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Iscrizione manuale</h1>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem', marginTop: '0.25rem' }}>
          Inserisci un partecipante direttamente, senza vincoli di posti disponibili.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>

          {/* ── Dati personali ── */}
          <div className="card">
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1.25rem' }}>Dati partecipante</h2>

            {submitError && (
              <div className="alert alert-danger" style={{ marginBottom: '1.25rem' }}>{submitError}</div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Nome <span className="required">*</span></label>
                <input
                  className={`form-control ${errors.nome ? 'is-invalid' : ''}`}
                  value={nome}
                  onChange={e => { setNome(e.target.value); setErrors(er => ({ ...er, nome: '' })); }}
                  placeholder="Mario"
                  autoComplete="off"
                />
                {errors.nome && <span className="form-error">{errors.nome}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Cognome <span className="required">*</span></label>
                <input
                  className={`form-control ${errors.cognome ? 'is-invalid' : ''}`}
                  value={cognome}
                  onChange={e => { setCognome(e.target.value); setErrors(er => ({ ...er, cognome: '' })); }}
                  placeholder="Rossi"
                  autoComplete="off"
                />
                {errors.cognome && <span className="form-error">{errors.cognome}</span>}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email <span className="required">*</span></label>
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                value={email}
                onChange={e => { setEmail(e.target.value); setErrors(er => ({ ...er, email: '' })); }}
                placeholder="mario.rossi@azienda.it"
                autoComplete="off"
              />
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Azienda / Ente</label>
              <input
                className="form-control"
                value={azienda}
                onChange={e => setAzienda(e.target.value)}
                placeholder="(facoltativo)"
                autoComplete="off"
              />
            </div>

            {/* Custom fields */}
            {visibleCustomFields.map(field => (
              <div key={field.key} className="form-group">
                <label className="form-label">
                  {field.label}{field.required && <span className="required"> *</span>}
                </label>
                {field.type === 'select' ? (
                  <>
                    <select
                      className={`form-control ${errors[`extra_${field.key}`] ? 'is-invalid' : ''}`}
                      value={String(extraData[field.key] ?? '')}
                      onChange={e => {
                        setExtraData(d => ({ ...d, [field.key]: e.target.value }));
                        setErrors(er => ({ ...er, [`extra_${field.key}`]: '' }));
                      }}
                    >
                      <option value="">Seleziona...</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>{opt === 'Altro' ? 'Altro...' : opt}</option>
                      ))}
                    </select>
                    {extraData[field.key] === 'Altro' && (
                      <input
                        className="form-control"
                        style={{ marginTop: '0.5rem' }}
                        placeholder="Specifica..."
                        value={String(extraData[`${field.key}__altro`] ?? '')}
                        onChange={e => setExtraData(d => ({ ...d, [`${field.key}__altro`]: e.target.value }))}
                      />
                    )}
                  </>
                ) : field.type === 'checkbox' ? (
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id={`extra_${field.key}`}
                      className="form-check-input"
                      checked={Boolean(extraData[field.key])}
                      onChange={e => {
                        setExtraData(d => ({ ...d, [field.key]: e.target.checked }));
                        setErrors(er => ({ ...er, [`extra_${field.key}`]: '' }));
                      }}
                    />
                    <label htmlFor={`extra_${field.key}`} className="form-check-label">{field.label}</label>
                  </div>
                ) : field.type === 'textarea' ? (
                  <textarea
                    className={`form-control ${errors[`extra_${field.key}`] ? 'is-invalid' : ''}`}
                    rows={3}
                    value={String(extraData[field.key] ?? '')}
                    placeholder={field.placeholder}
                    onChange={e => {
                      setExtraData(d => ({ ...d, [field.key]: e.target.value }));
                      setErrors(er => ({ ...er, [`extra_${field.key}`]: '' }));
                    }}
                  />
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    className={`form-control ${errors[`extra_${field.key}`] ? 'is-invalid' : ''}`}
                    value={String(extraData[field.key] ?? '')}
                    placeholder={field.placeholder}
                    onChange={e => {
                      setExtraData(d => ({ ...d, [field.key]: e.target.value }));
                      setErrors(er => ({ ...er, [`extra_${field.key}`]: '' }));
                    }}
                  />
                )}
                {errors[`extra_${field.key}`] && (
                  <span className="form-error">{errors[`extra_${field.key}`]}</span>
                )}
              </div>
            ))}
          </div>

          {/* ── Selezione edizioni ── */}
          <div className="card">
            <h2 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Edizioni
              {selectedIds.size > 0 && (
                <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: 'var(--gray-500)', marginLeft: '0.5rem' }}>
                  ({selectedIds.size} selezionate)
                </span>
              )}
            </h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
              Tutte le edizioni sono selezionabili, indipendentemente da posti disponibili o stato di apertura.
            </p>

            {errors.editions && (
              <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{errors.editions}</div>
            )}

            {isLoading ? (
              <div className="loading-container"><div className="spinner" /></div>
            ) : courses.length === 0 ? (
              <p style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>Nessun corso trovato.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {courses.map(course => {
                  const courseEditions = (editionsByCourse.get(course.id) ?? [])
                    .slice()
                    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
                  if (courseEditions.length === 0) return null;
                  return (
                    <div key={course.id}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {course.name}
                        {!course.active && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 400, textTransform: 'none' }}>(inattivo)</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                        {courseEditions.map(edition => {
                          const checked = selectedIds.has(edition.id);
                          const date = new Date(edition.scheduledAt).toLocaleString('it-IT', {
                            timeZone: 'Europe/Rome',
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          });
                          return (
                            <label
                              key={edition.id}
                              style={{
                                display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
                                padding: '0.625rem 0.75rem', borderRadius: 8, cursor: 'pointer',
                                border: `1px solid ${checked ? 'var(--primary)' : 'var(--gray-200)'}`,
                                background: checked ? 'var(--primary-50, #f0f4ff)' : '#fff',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleEdition(edition.id)}
                                style={{ marginTop: '0.15rem', accentColor: 'var(--primary)', flexShrink: 0 }}
                              />
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--gray-800)' }}>
                                  {edition.label}
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                                  {date}
                                  {!edition.registrationOpen && (
                                    <span style={{ marginLeft: '0.5rem', color: 'var(--warning, #d97706)' }}>· iscrizioni chiuse</span>
                                  )}
                                  {edition.enrollmentCount !== undefined && (
                                    <span style={{ marginLeft: '0.5rem', color: 'var(--gray-400)' }}>
                                      · {edition.enrollmentCount}/{edition.maxParticipants} posti
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <><span className="spinner" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }} /> Salvataggio...</>
            ) : (
              `Iscrivi${selectedIds.size > 0 ? ` (${selectedIds.size} ${selectedIds.size === 1 ? 'edizione' : 'edizioni'})` : ''}`
            )}
          </button>
          <button type="button" className="btn btn-ghost" onClick={handleReset}>
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
}
